<template>
  <section class="legal-page">
    <!-- Hero Section -->
    <div class="legal-hero">
      <div class="hero-content">
        <h1 class="hero-title">
          {{ $t?.('legal.title') || 'Aviso Legal' }}
        </h1>
        <p class="hero-subtitle">
          {{ $t?.('legal.subtitle') || 'Información legal' }}
        </p>
      </div>
    </div>

    <!-- Content Section -->
    <div class="legal-content section">
      <div class="container">
        <Card variant="elevated" padding="lg" class="content-card">
          <div class="legal-inner-content">
            
            <!-- LSSI -->
            <div class="legal-section">
              <h2 class="section-title">
                {{ $t?.('legal.lssiTitle') || 'Lei dos Servizos da Sociedade da Información (LSSI)' }}
              </h2>
              <div class="section-text" v-html="$t?.('legal.lssiText1') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.lssiText2') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.lssiText3') || ''"></div>
            </div>

            <!-- Datos Identificativos -->
            <div class="legal-section">
              <h2 class="section-title">
                1. {{ $t?.('legal.identityTitle') || 'Datos Identificativos' }}
              </h2>
              
              <div class="identity-box">
                <div class="identity-item">
                  <i class="fas fa-globe"></i>
                  <div class="identity-details">
                    <strong>{{ $t?.('legal.domain') || 'Nome de dominio' }}</strong>
                    <span>bandadenoia.gal</span>
                  </div>
                </div>
                
                <div class="identity-item">
                  <i class="fas fa-building"></i>
                  <div class="identity-details">
                    <strong>{{ $t?.('legal.tradeName') || 'Nome comercial' }}</strong>
                    <span>Banda de Música de Noia</span>
                  </div>
                </div>
                
                <div class="identity-item">
                  <i class="fas fa-certificate"></i>
                  <div class="identity-details">
                    <strong>{{ $t?.('legal.legalName') || 'Denominación social' }}</strong>
                    <span>Banda de Música de Noia</span>
                  </div>
                </div>
                
                <!-- NIF eliminado a petición -->
                
                <div class="identity-item">
                  <i class="fas fa-map-marker-alt"></i>
                  <div class="identity-details">
                    <strong>{{ $t?.('legal.address') || 'Domicilio social' }}</strong>
                    <span>Rúa Vizconde de San Alberto, S/N, 15200 Noia, A Coruña</span>
                  </div>
                </div>
                
                <div class="identity-item">
                  <i class="fas fa-envelope"></i>
                  <div class="identity-details">
                    <strong>Email</strong>
                    <a href="#" @click.prevent="copyEmail" class="email-link">
                      bandadenoia<span class="email-separator"> [arroba] </span>gmail.com
                    </a>
                    <span class="email-hint">(Click para copiar)</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Propiedad Intelectual -->
            <div class="legal-section">
              <h2 class="section-title">
                2. {{ $t?.('legal.intellectualTitle') || 'Dereitos de Propiedade Intelectual e Industrial' }}
              </h2>
              <div class="section-text" v-html="$t?.('legal.intellectualText1') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.intellectualText2') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.intellectualText3') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.intellectualText4') || ''"></div>

              <div class="info-box">
                <i class="fas fa-exclamation-triangle"></i>
                <div>
                  <p><strong>Observacións sobre propiedade intelectual</strong></p>
                  <p>Para realizar calquera tipo de observación respecto a posibles incumprimentos dos dereitos de propiedade intelectual ou industrial, pode facelo a través do correo electrónico 
                    <a href="#" @click.prevent="copyEmail" class="email-link">
                      bandadenoia<span class="email-separator"> [arroba] </span>gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <!-- Exención de Responsabilidades -->
            <div class="legal-section">
              <h2 class="section-title">
                3. {{ $t?.('legal.liabilityTitle') || 'Exención de Responsabilidades' }}
              </h2>
              <div class="section-text" v-html="$t?.('legal.liabilityText') || ''"></div>

              <h3 class="subsection-title">
                <i class="fas fa-cookie-bite"></i>
                {{ $t?.('legal.cookiesTitle') || 'Uso de Cookies' }}
              </h3>
              <div class="section-text" v-html="$t?.('legal.cookiesText1') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.cookiesText2') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.cookiesText3') || ''"></div>
              
              <div class="info-box">
                <i class="fas fa-info-circle"></i>
                <div>
                  <p>Para máis información sobre cookies, consulta a nosa 
                    <router-link to="/cookies" class="link-inline">Política de Cookies</router-link>.
                  </p>
                </div>
              </div>

              <h3 class="subsection-title">
                <i class="fas fa-link"></i>
                {{ $t?.('legal.linksTitle') || 'Política de Enlaces' }}
              </h3>
              <div class="section-text" v-html="$t?.('legal.linksText1') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.linksText2') || ''"></div>
              <div class="section-text" v-html="$t?.('legal.linksText3') || ''"></div>

              <h3 class="subsection-title">
                <i class="fas fa-network-wired"></i>
                {{ $t?.('legal.ipTitle') || 'Enderezos IP' }}
              </h3>
              <div class="section-text" v-html="$t?.('legal.ipText') || ''"></div>
            </div>

            <!-- Ley Aplicable -->
            <div class="legal-section">
              <h2 class="section-title">
                4. {{ $t?.('legal.lawTitle') || 'Lei Aplicable e Xurisdición' }}
              </h2>
              <div class="section-text" v-html="$t?.('legal.lawText') || ''"></div>

              <div class="regulations-box">
                <h4><i class="fas fa-gavel"></i> Normativa Aplicable</h4>
                <ul class="regulations-list">
                  <li>
                    <strong>LSSI:</strong> Lei 34/2002, do 11 de xullo, de Servizos da Sociedade da Información e de Comercio Electrónico
                  </li>
                  <li>
                    <strong>RXPD:</strong> Regulamento (UE) 2016/679 do Parlamento Europeo e do Consello, do 27 de abril de 2016
                  </li>
                  <li>
                    <strong>LOPDGDD:</strong> Lei Orgánica 3/2018, do 5 de decembro, de Protección de Datos e Garantía dos Dereitos Dixitais
                  </li>
                </ul>
              </div>
            </div>

            <!-- Last Update -->
            <div class="last-update">
              <i class="fas fa-calendar-alt"></i>
              {{ $t?.('legal.lastUpdate') || 'Última actualización' }}: {{ currentDate }}
            </div>
          </div>
        </Card>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import { useHead } from '@vueuse/head';
import Card from '../ui/Card.vue';
import { buildHeadConfig, getBreadcrumbJsonLd } from '../../seo/meta';

// Access i18n
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;

useHead(
  buildHeadConfig('legal', {
    jsonLd: [getBreadcrumbJsonLd('legal')],
  }),
)

// Current date for last update
const currentDate = computed(() => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}-${month}-${year}`;
});

// Copy email function
const copyEmail = async () => {
  const email = 'bandadenoia@gmail.com';
  try {
    await navigator.clipboard.writeText(email);
    alert('✓ Email copiado ao portapapeis!');
  } catch (err) {
    const textArea = document.createElement('textarea');
    textArea.value = email;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    alert('✓ Email copiado ao portapapeis!');
  }
};
</script>

<style scoped>
/* Legal Page */
.legal-page {
  background: white;
}

/* Hero Section */
.legal-hero {
  position: relative;
  min-height: 35vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-band);
  overflow: hidden;
  padding: var(--spacing-16) var(--spacing-4);
}

.legal-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: var(--font-weight-bold);
  color: white;
  margin-bottom: var(--spacing-4);
  font-family: var(--font-family-serif);
  line-height: 1.1;
  text-align: center;
}

.hero-subtitle {
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: rgba(255, 255, 255, 0.95);
  font-weight: var(--font-weight-medium);
  text-align: center;
}

/* Content Section */
.legal-content {
  padding: var(--spacing-20) 0;
  background: var(--color-neutral-50);
}

.content-card {
  max-width: 1200px;
  margin: 0 auto;
}

.legal-inner-content {
  color: var(--color-text-primary);
  line-height: 1.8;
  padding: var(--spacing-8);
}

/* Sections */
.legal-section {
  margin-bottom: var(--spacing-12);
  padding-bottom: var(--spacing-8);
  border-bottom: 1px solid var(--color-neutral-200);
}

.legal-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin: 0 0 var(--spacing-6) 0;
  font-family: var(--font-family-serif);
}

.subsection-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary-600);
  margin: var(--spacing-6) 0 var(--spacing-4) 0;
  font-family: var(--font-family-serif);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.subsection-title i {
  color: var(--color-primary-500);
}

.section-text {
  margin-bottom: var(--spacing-4);
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  text-align: justify;
  line-height: 1.8;
}

/* Identity Box */
.identity-box {
  background: var(--color-neutral-50);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-8);
  border: 1px solid var(--color-neutral-200);
}

.identity-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-4);
  padding: var(--spacing-4) 0;
  border-bottom: 1px solid var(--color-neutral-200);
}

.identity-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.identity-item i {
  font-size: 1.25rem;
  color: var(--color-primary-500);
  width: 24px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.identity-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
}

.identity-details strong {
  color: var(--color-primary-700);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.identity-details span {
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
}

.email-link {
  color: var(--color-primary-600);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: inline;
}

.email-link:hover {
  text-decoration: underline;
}

.email-separator {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
}

.email-hint {
  display: inline-block;
  margin-left: var(--spacing-2);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-style: italic;
}

/* Info Box */
.info-box {
  background: var(--color-secondary-50);
  border: 1px solid var(--color-secondary-200);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-6);
  margin: var(--spacing-10) 0;
  border-left: 4px solid var(--color-secondary-500);
  display: flex;
  gap: var(--spacing-4);
  align-items: flex-start;
}

.info-box i {
  font-size: 1.5rem;
  color: var(--color-secondary-600);
  flex-shrink: 0;
  margin-top: 2px;
}

.info-box p {
  margin: var(--spacing-2) 0;
  color: var(--color-text-secondary);
}

.info-box strong {
  color: var(--color-secondary-700);
}

.link-inline {
  color: var(--color-primary-600);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
}

.link-inline:hover {
  text-decoration: underline;
}

/* Regulations Box */
.regulations-box {
  background: var(--color-primary-50);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-6);
  margin: var(--spacing-6) 0;
  border: 1px solid var(--color-primary-200);
}

.regulations-box h4 {
  margin: 0 0 var(--spacing-4) 0;
  color: var(--color-primary-700);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-lg);
}

.regulations-box h4 i {
  color: var(--color-primary-500);
}

.regulations-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.regulations-list li {
  padding: var(--spacing-4);
  margin-bottom: var(--spacing-3);
  background: white;
  border-radius: var(--border-radius-md);
  border-left: 4px solid var(--color-primary-500);
  box-shadow: var(--shadow-sm);
}

.regulations-list li:last-child {
  margin-bottom: 0;
}

.regulations-list li strong {
  color: var(--color-primary-700);
  display: block;
  margin-bottom: var(--spacing-2);
  font-size: var(--font-size-base);
}

/* Last Update */
.last-update {
  margin-top: var(--spacing-10);
  padding: var(--spacing-4) var(--spacing-6);
  background: rgba(79, 148, 160, 0.1);
  border-left: 4px solid var(--color-primary-500);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-primary-700);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-weight: var(--font-weight-medium);
}

.last-update i {
  color: var(--color-primary-500);
}

/* Responsive */
@media (max-width: 768px) {
  .legal-hero {
    min-height: 30vh;
  }

  .section-title {
    font-size: var(--font-size-xl);
  }

  .subsection-title {
    font-size: var(--font-size-lg);
  }

  .section-text {
    text-align: left;
  }

  .identity-item {
    flex-direction: column;
    gap: var(--spacing-2);
  }

  .identity-item i {
    margin-top: 0;
  }

  .info-box {
    flex-direction: column;
    gap: var(--spacing-3);
  }

  .info-box i {
    margin-top: 0;
  }
}

/* Refresh 2026 - visual coherence with Home */
.legal-page {
  background: transparent;
}

.legal-hero {
  min-height: 40vh;
  padding: clamp(2.4rem, 5.4vw, 4.3rem) var(--spacing-4);
  background:
    radial-gradient(circle at 84% 14%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 42%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
}

.legal-hero::before {
  background: radial-gradient(circle at 18% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 45%);
}

.legal-content {
  background: transparent;
}

.content-card {
  background: var(--surface-card-strong);
  border: 1px solid rgba(16, 41, 59, 0.12);
  box-shadow: var(--shadow-xl);
}

.identity-box,
.regulations-box,
.info-box {
  background: rgba(255, 255, 255, 0.7);
  border-color: rgba(16, 41, 59, 0.12);
}

.regulations-list li {
  background: rgba(255, 255, 255, 0.88);
}

.last-update {
  background: rgba(26, 79, 113, 0.12);
}
</style>
